import React, { useMemo, useState, useRef } from "react";
import PaginationBar from "../shared/PaginationBar/PaginationBar";

type SortField = "serial" | "time";

interface SerialSelectionListProps {
    serials: any[];
    selectedIds: Set<string>;
    totalCount: number;
    page: number;
    pageSize: number;
    isLoading: boolean;
    onToggleSerial: (id: string) => void;
    onToggleSelectAll: () => void;
    onSelectRange: (ids: string[]) => void;
    onUnselectAll: () => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    searchValue: string;
    onSearchChange: (value: string) => void;
    selectionDisabled?: boolean;
}

const SerialSelectionList: React.FC<SerialSelectionListProps> = ({
    serials,
    selectedIds,
    totalCount,
    page,
    pageSize,
    isLoading,
    onToggleSerial,
    onToggleSelectAll,
    onSelectRange,
    onUnselectAll,
    onPageChange,
    onPageSizeChange,
    searchValue,
    onSearchChange,
    selectionDisabled = false,
}) => {
    const [sortField, setSortField] = useState<SortField>("serial");
    const [rangeMode, setRangeMode] = useState(false);
    const rangeStartRef = useRef<string | null>(null);

    // Search is applied server-side (across all pages); here we only sort the current page.
    const filteredSerials = useMemo(() => {
        const sorted = [...serials];
        switch (sortField) {
            case "serial":
                sorted.sort((a, b) => (a.value || "").localeCompare(b.value || ""));
                break;
            case "time":
                sorted.sort((a, b) => {
                    const aTime = a.current_step_entered_at || a.updated_at || "";
                    const bTime = b.current_step_entered_at || b.updated_at || "";
                    return aTime.localeCompare(bTime);
                });
                break;
        }

        return sorted;
    }, [serials, sortField]);

    const allSelected = filteredSerials.length > 0 && filteredSerials.every((s) => selectedIds.has(String(s.id)));

    return (
        <div className="sf-serial-list">
            <div className="sf-serial-list__controls">
                <input
                    type="text"
                    className="form-control sf-serial-list__search"
                    placeholder="Search serials..."
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                />
                <select
                    className="form-select sf-serial-list__sort"
                    value={sortField}
                    onChange={(e) => setSortField(e.target.value as SortField)}>
                    <option value="serial">Sort: Serial #</option>
                    <option value="time">Sort: Oldest First</option>
                </select>
            </div>

            {!selectionDisabled && (
                <div className="sf-serial-list__select-bar">
                    <input type="checkbox" className="sf-serial-list__checkbox" checked={allSelected} onChange={onToggleSelectAll} />
                    <span className="sf-serial-list__select-label" onClick={onToggleSelectAll}>
                        Select All ({filteredSerials.length})
                    </span>
                    <button
                        className={`btn btn-sm sf-serial-list__range-btn ${rangeMode ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => {
                            setRangeMode(!rangeMode);
                            rangeStartRef.current = null;
                        }}>
                        <i className="fas fa-arrows-alt-v me-1" />
                        {rangeMode ? "Range On" : "Range"}
                    </button>
                    {selectedIds.size > 0 && (
                        <button className="btn btn-sm btn-outline-danger sf-serial-list__range-btn" onClick={onUnselectAll}>
                            <i className="fas fa-times me-1" />
                            Unselect All
                        </button>
                    )}
                    {selectedIds.size > 0 && <span className="sf-serial-list__count">{selectedIds.size} selected</span>}
                </div>
            )}

            <div className="sf-serial-list__rows">
                {isLoading ? (
                    <div className="sf-serial-list__loading">
                        <div className="spinner-border spinner-border-sm" role="status" />
                        <span className="ms-2">Loading serials...</span>
                    </div>
                ) : filteredSerials.length === 0 ? (
                    <div className="sf-serial-list__empty">No serials at this step</div>
                ) : (
                    filteredSerials.map((serial) => {
                        const serialKey = String(serial.id);
                        const handleRowClick = () => {
                            if (selectionDisabled) {
                                return;
                            }
                            if (rangeMode) {
                                if (!rangeStartRef.current) {
                                    rangeStartRef.current = serialKey;
                                    onToggleSerial(serialKey);
                                } else {
                                    const startIdx = filteredSerials.findIndex((s) => String(s.id) === rangeStartRef.current);
                                    const endIdx = filteredSerials.findIndex((s) => String(s.id) === serialKey);
                                    const from = Math.min(startIdx, endIdx);
                                    const to = Math.max(startIdx, endIdx);
                                    const rangeKeys = filteredSerials.slice(from, to + 1).map((s) => String(s.id));
                                    onSelectRange(rangeKeys);
                                    rangeStartRef.current = null;
                                }
                            } else {
                                onToggleSerial(serialKey);
                            }
                        };
                        return (
                            <div
                                key={serialKey}
                                className={`sf-serial-list__row ${!selectionDisabled && selectedIds.has(serialKey) ? "sf-serial-list__row--selected" : ""}${!selectionDisabled && rangeMode && rangeStartRef.current === serialKey ? " sf-serial-list__row--range-start" : ""}`}
                                onClick={handleRowClick}>
                                {!selectionDisabled && (
                                    <input
                                        type="checkbox"
                                        className="sf-serial-list__checkbox"
                                        checked={selectedIds.has(serialKey)}
                                        onChange={handleRowClick}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                )}
                                <div className="sf-serial-list__info">
                                    <span className="sf-serial-list__value">{serial.value}</span>
                                    <span className="sf-serial-list__item-name">
                                        {serial.Catalog_Item?.name || ""}
                                        {serial.Catalog_Item?.caliber ? ` - ${serial.Catalog_Item.caliber}` : ""}
                                        {serial.Order?.name ? ` - ${serial.Order.name}` : ""}
                                        {serial.Order?.Vendor?.name ? ` - ${serial.Order.Vendor.name}` : ""}
                                    </span>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {totalCount > 0 && (
                <div className="sf-serial-list__pagination">
                    <PaginationBar
                        offset={page}
                        limit={pageSize}
                        totalRecords={totalCount}
                        onPaginationChanged={(newPage: number, newLimit: number) => {
                            onPageChange(newPage);
                            onPageSizeChange(newLimit);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default SerialSelectionList;
